import React, { useEffect, useRef, useState } from 'react';

// Singleton promise — safe when multiple VehicleMap instances mount simultaneously.
let _leafletReady = null;
function loadLeaflet() {
  if (_leafletReady) return _leafletReady;
  _leafletReady = new Promise((resolve) => {
    if (window.L) { resolve(); return; }
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.css';
      document.head.appendChild(link);
    }
    const existing = document.getElementById('leaflet-js');
    if (existing) { existing.addEventListener('load', resolve); return; }
    const s = document.createElement('script');
    s.id = 'leaflet-js';
    s.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.js';
    s.onload = resolve;
    document.head.appendChild(s);
  });
  return _leafletReady;
}

function markerHtml(color, direction) {
  return `<div style="
    width:20px;height:20px;background:${color};
    border-radius:50% 50% 50% 0;
    transform:rotate(${(direction || 0) - 45}deg);
    border:2px solid white;
    box-shadow:0 2px 6px rgba(0,0,0,.4);">
  </div>`;
}

// pos shape: { lat, lng, speed, direction, online, gpsTime }
// size: 'mini' (fills parent height) | 'full' (400px fixed)
export default function VehicleMap({ pos, size = 'mini' }) {
  const mapRef     = useRef(null);
  const mapInst    = useRef(null);
  const markerInst = useRef(null);
  const [ready, setReady] = useState(false);

  // Initialise map once on mount.
  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then(() => {
      if (cancelled || !mapRef.current || mapInst.current) return;
      const L   = window.L;
      const lat = pos?.lat ?? 3.139;
      const lng = pos?.lng ?? 101.687;
      const map = L.map(mapRef.current, {
        center: [lat, lng], zoom: 15,
        zoomControl: false, attributionControl: false,
        dragging: false, scrollWheelZoom: false,
        doubleClickZoom: false, touchZoom: false,
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);
      mapInst.current = map;
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (mapInst.current) {
        mapInst.current.remove();
        mapInst.current  = null;
        markerInst.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update marker whenever pos changes (every 15 s from parent).
  useEffect(() => {
    if (!ready || !mapInst.current || !pos?.lat || !pos?.lng) return;
    const L     = window.L;
    const color = pos.online
      ? (pos.speed > 0 ? '#185FA5' : '#4CAF50')
      : '#888';
    const icon  = L.divIcon({
      className: '',
      html:      markerHtml(color, pos.direction),
      iconSize:  [20, 20],
      iconAnchor:[10, 20],
    });
    if (markerInst.current) {
      markerInst.current.setLatLng([pos.lat, pos.lng]).setIcon(icon);
    } else {
      markerInst.current = L.marker([pos.lat, pos.lng], { icon }).addTo(mapInst.current);
    }
    mapInst.current.setView([pos.lat, pos.lng], 15, { animate: true, duration: 0.5 });
  }, [ready, pos]);

  const containerStyle = {
    position: 'relative',
    height: size === 'full' ? 400 : '100%',
    overflow: 'hidden',
    background: '#111',
  };

  if (!pos) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#444', fontSize: 11 }}>No GPS</span>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      {ready && (
        <div style={{
          position: 'absolute', bottom: 4, left: 4, zIndex: 1000, pointerEvents: 'none',
          background: 'rgba(0,0,0,.65)', color: '#ccc', fontSize: 9,
          padding: '2px 5px', borderRadius: 3,
        }}>
          {pos.speed > 0 ? `${pos.speed} km/h` : 'Idle'}
          {pos.gpsTime ? ` · ${pos.gpsTime.substring(11, 16)}` : ''}
        </div>
      )}
    </div>
  );
}
